import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Receipt,
  Building2,
  ClipboardList,
  Ship,
  Package,
  Boxes,
  Users as UsersIcon,
  Truck,
  Target,
  TrendingUp,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  FileBarChart,
  Shield,
  BarChart3,
  FileSearch,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";

const navigationItems = [
  {
    title: "FINANCEIRO",
    items: [
      { name: "Dashboard", href: "/", icon: LayoutDashboard },
      { name: "Outras Empresas", href: "/outras-empresas", icon: BarChart3, outrasEmpresasOnly: true },
      { name: "Pesquisa Comercial", href: "https://gestao.wellpec.com.br/petronect/login", icon: FileSearch, petronectOnly: true, external: true },
      { name: "Notas Fiscais", href: "/notas-fiscais", icon: Receipt },
      { name: "Contas Bancárias", href: "/contas-bancarias", icon: Building2 },
      { name: "Relatórios", href: "/relatorios", icon: FileBarChart },
    ],
  },
  {
    title: "OPERACIONAL",
    items: [
      { name: "Atividades", href: "/atividades", icon: ClipboardList },
      { name: "Importações", href: "/importacoes", icon: Ship },
    ],
  },
  {
    title: "PORTFÓLIO",
    items: [
      { name: "Produtos", href: "/produtos", icon: Package },
      { name: "Estoque", href: "/estoque", icon: Boxes },
      { name: "Fornecedores", href: "/fornecedores", icon: Truck },
      { name: "Concorrentes", href: "/concorrentes", icon: Target },
      { name: "Inteligência de Mercado", href: "/inteligencia-mercado", icon: TrendingUp },
    ],
  },
  {
    title: "CADASTROS",
    items: [
      { name: "Clientes", href: "/clientes", icon: UsersIcon },
      { name: "Auditoria", href: "/auditoria", icon: Shield },
      { name: "Configurações", href: "/configuracoes", icon: Settings },
    ],
  },
];

export function Sidebar({ collapsed, onToggle }) {
  const { user, logout, canAccessOutrasEmpresas, canViewPetronect } = useAuth();

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-[#0f172a] text-slate-100 transition-all duration-300 flex flex-col",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-slate-700 px-4 flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded bg-[#ea580c] flex items-center justify-center">
              <span className="font-heading font-bold text-white">OG</span>
            </div>
            <span className="font-heading font-semibold text-lg">ERP</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="text-slate-400 hover:text-white hover:bg-slate-800"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 min-h-0">
        {navigationItems.map((section) => (
          <div key={section.title} className="mb-4">
            {!collapsed && (
              <h3 className="px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            <ul className="space-y-1 px-2">
              {section.items
                .filter((item) => !item.outrasEmpresasOnly || canAccessOutrasEmpresas())
                .filter((item) => !item.petronectOnly || canViewPetronect())
                .map((item) => (
                <li key={item.href}>
                  {item.external ? (
                    <a
                      href={item.href}
                      className="flex items-center gap-3 rounded-sm px-3 py-2 text-sm text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!collapsed && <span>{item.name}</span>}
                    </a>
                  ) : (
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors",
                          isActive
                            ? "bg-[#ea580c] text-white"
                            : "text-slate-400 hover:text-white hover:bg-slate-800"
                        )
                      }
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!collapsed && <span>{item.name}</span>}
                    </NavLink>
                  )}
                </li>
                ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-slate-700 p-4 flex-shrink-0">
        {!collapsed && (
          <div className="mb-3">
            <p className="text-sm font-medium text-white truncate">{user?.nome}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.perfil}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "default"}
          onClick={logout}
          className="w-full text-slate-400 hover:text-white hover:bg-slate-800 justify-start"
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span className="ml-3">Sair</span>}
        </Button>
      </div>
    </aside>
  );
}
